/* Rossmonster scroll behaviors — reveal, parallax, hotspot popovers.
   Loaded conditionally from theme.liquid only when ros_context is true.
   All motion respects prefers-reduced-motion.
*/
(function () {
  'use strict';

  if (!document.body.classList.contains('is-rossmonster')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ------------------------------------------------------------------
  // 1. Fade-up reveal on scroll
  // ------------------------------------------------------------------
  function initReveal() {
    if (reduceMotion) return;
    var els = document.querySelectorAll('.ros-reveal');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries, observer) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  // ------------------------------------------------------------------
  // 2. Parallax — translateY proportional to scroll position
  // ------------------------------------------------------------------
  function initParallax() {
    if (reduceMotion) return;
    var els = document.querySelectorAll('[data-ros-parallax]');
    if (!els.length) return;

    var ticking = false;
    var vh = window.innerHeight;

    function update() {
      els.forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var strength = parseFloat(el.dataset.rosParallax) || 0.15;
        var offset = (rect.top + rect.height / 2 - vh / 2) * strength * -1;
        el.style.setProperty('--ros-parallax-y', offset.toFixed(1) + 'px');
      });
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { vh = window.innerHeight; update(); }, { passive: true });
    update();
  }

  // ------------------------------------------------------------------
  // 3. Hotspot popovers — click dot to open product card, click outside / Escape to close
  // ------------------------------------------------------------------
  function initHotspots() {
    var stages = document.querySelectorAll('.ros-hotspot__stage');
    if (!stages.length) return;

    stages.forEach(function (stage) {
      var dots = stage.querySelectorAll('.ros-hotspot__dot');
      var cards = stage.querySelectorAll('.ros-hotspot__card');

      // Auto-resolve card side based on dot position when set to 'auto'
      dots.forEach(function (dot, i) {
        var side = dot.dataset.side;
        var card = cards[i];
        if (!card) return;
        if (side === 'auto' || !side) {
          var leftPct = parseFloat(dot.style.left) || 50;
          card.dataset.resolvedSide = leftPct < 50 ? 'right' : 'left';
        } else {
          card.dataset.resolvedSide = side;
        }
      });

      function closeAll(except) {
        cards.forEach(function (c) {
          if (c !== except) {
            c.hidden = true;
            c.classList.remove('is-open');
          }
        });
        dots.forEach(function (d) { d.setAttribute('aria-expanded', 'false'); });
      }

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          var card = cards[i];
          if (!card) return;
          var isOpen = !card.hidden;
          closeAll(isOpen ? null : card);
          if (!isOpen) {
            card.hidden = false;
            // Force reflow so transition runs
            void card.offsetWidth;
            card.classList.add('is-open');
            dot.setAttribute('aria-expanded', 'true');
          }
        });
      });

      // Close on outside click
      document.addEventListener('click', function (e) {
        if (!stage.contains(e.target)) closeAll(null);
      });

      // Close on Escape
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAll(null);
      });
    });
  }

  // ------------------------------------------------------------------
  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    initReveal();
    initParallax();
    initHotspots();
  });
})();

/* =============================================================================
   ROSSMONSTER — Inline-style enforcement for search input + filter sidebar.

   Why JS not CSS: Impact's <search-drawer> and <facet-form> compute their
   text color from `rgb(var(--text-color))`, where --text-color is set at
   runtime by Impact's section color-scheme system. body.is-rossmonster CSS
   rules lose the cascade race every time. Inline element.style.setProperty(
   prop, val, 'important') beats every external rule unconditionally.

   Palette: ember orange #F26A1F (accent), hard-black #0A0A0A (ink), race-
   shop white #FFFFFF. Search drawer = black-on-white. Filter sidebar sits
   on a dark backdrop so labels go WHITE with ember-orange group titles.

   This is the canonical sub-brand override pattern. Mirror of drm-scroll.js
   `fixSearch()` / `fixFilters()` with the Rossmonster palette.
   ============================================================================= */
(function () {
  'use strict';

  if (!document.body.classList.contains('is-rossmonster')) return;

  // Rossmonster palette
  var BLACK     = '#0A0A0A';
  var BLACK0    = '#000000';
  var EMBER     = '#F26A1F';
  var EMBER_DEEP = '#C5470A';
  var WHITE     = '#FFFFFF';
  var WHITE_DIM = 'rgba(255, 255, 255, 0.65)';
  var BG        = '#FFFFFF';

  function force(el, prop, val) {
    if (el && el.style) { el.style.setProperty(prop, val, 'important'); }
  }

  // ------- Search inputs (header drawer + predictive search) -------
  function fixSearch(root) {
    var scope = root || document;

    scope.querySelectorAll(
      'input[type="search"], input[name="q"], .search-input > input, ' +
      'search-drawer input, .search-drawer input, predictive-search input'
    ).forEach(function (input) {
      force(input, 'color', BLACK0);
      force(input, '-webkit-text-fill-color', BLACK0);
      force(input, 'background', BG);
      force(input, 'background-color', BG);
      force(input, 'caret-color', EMBER);
      force(input, 'font-family', "'IBM Plex Mono', monospace");
    });

    scope.querySelectorAll(
      'search-drawer, .search-drawer, .search-input, predictive-search, ' +
      '.predictive-search'
    ).forEach(function (host) {
      force(host, '--text-color',                    '10, 10, 10');
      force(host, '--text-primary',                  '10, 10, 10');
      force(host, '--dialog-text-color',             '10, 10, 10');
      force(host, '--header-text-color',             '10, 10, 10');
      force(host, '--header-transparent-text-color', '10, 10, 10');
      force(host, 'color', BLACK0);
    });

    scope.querySelectorAll(
      'search-drawer .text-subdued, .search-drawer .text-subdued, ' +
      'search-drawer button, .search-drawer button'
    ).forEach(function (btn) {
      force(btn, 'color', BLACK);
    });

    scope.querySelectorAll(
      '.header__main-nav a[aria-controls="search-drawer"], ' +
      '.header__icon-list a[href*="/search"], ' +
      '.header__search, .header__search *'
    ).forEach(function (el) {
      force(el, 'color', WHITE);
      if (el.querySelectorAll) {
        el.querySelectorAll('svg').forEach(function (svg) {
          force(svg, 'color', WHITE);
          force(svg, 'fill',  WHITE);
          force(svg, 'stroke', WHITE);
        });
      }
    });
  }

  // ------- Collection filter sidebar (facets-vertical) -------
  // Rossmonster collection page sits on dark backdrop. Labels go WHITE,
  // group titles go EMBER ORANGE uppercase.
  function fixFilters(root) {
    var scope = root || document;

    scope.querySelectorAll(
      'facet-form, #facet-form, form[is="facet-form"], ' +
      '.facets-vertical, .facets-sidebar, .collection-filters, ' +
      '.filter-and-sort'
    ).forEach(function (host) {
      force(host, 'background', 'transparent');
      force(host, 'background-color', 'transparent');
      force(host, 'color', WHITE);
      force(host, '--text-color', '255, 255, 255');
      force(host, '--text-primary', '255, 255, 255');
      force(host, '--background', '0, 0, 0');
      force(host, 'border', 'none');
      force(host, 'box-shadow', 'none');

      host.querySelectorAll('*').forEach(function (el) {
        if (el.matches && el.matches('input[type="checkbox"], input[type="radio"]')) {
          force(el, 'accent-color', EMBER);
          return;
        }
        force(el, 'background-color', 'transparent');
        force(el, 'color', WHITE);
      });

      // Group titles — ember orange uppercase Barlow Condensed
      host.querySelectorAll(
        '.accordion__toggle, .accordion__toggle .bold, summary.bold, ' +
        'summary, .bold, .h5, .h6'
      ).forEach(function (lbl) {
        force(lbl, 'color', EMBER);
        force(lbl, 'font-family', "'Barlow Condensed', sans-serif");
        force(lbl, 'font-weight', '700');
        force(lbl, 'letter-spacing', '0.08em');
        force(lbl, 'text-transform', 'uppercase');
        force(lbl, 'font-size', '0.95rem');
      });

      // Filter value labels — white Inter
      host.querySelectorAll(
        'label, .checkbox-list label, .checkbox-list > *, ' +
        '.checkbox + span, input + span, span'
      ).forEach(function (lbl) {
        if (lbl.closest && lbl.closest('.accordion__toggle, summary')) return;
        force(lbl, 'color', WHITE);
        force(lbl, 'font-family', "'Inter', system-ui, sans-serif");
        force(lbl, 'font-weight', '400');
        force(lbl, 'text-transform', 'none');
        force(lbl, 'letter-spacing', '0');
        force(lbl, 'font-size', '0.9rem');
      });

      host.querySelectorAll('a').forEach(function (a) {
        if (a.closest && a.closest('.accordion__toggle, summary')) return;
        if (a.matches && a.matches('.active-facet, .facet-tag, a[is="facet-remove"]')) return;
        force(a, 'color', WHITE);
      });

      host.querySelectorAll('.count, .text-subdued, .price-range__divider').forEach(function (el) {
        force(el, 'color', WHITE_DIM);
      });

      host.querySelectorAll('svg').forEach(function (svg) {
        force(svg, 'color',  WHITE);
        force(svg, 'fill',   WHITE);
        force(svg, 'stroke', WHITE);
      });

      // Active filter pills — ember accent
      host.querySelectorAll(
        '.active-facets a, .active-facet, .facet-tag, a[is="facet-remove"]'
      ).forEach(function (pill) {
        force(pill, 'color', EMBER);
        force(pill, 'background-color', 'rgba(242, 106, 31, 0.15)');
        force(pill, 'border', '1px solid rgba(242, 106, 31, 0.50)');
      });

      // Number inputs (price range)
      host.querySelectorAll('input[type="number"], input[type="text"]').forEach(function (input) {
        force(input, 'color', WHITE);
        force(input, '-webkit-text-fill-color', WHITE);
        force(input, 'background-color', 'rgba(255, 255, 255, 0.06)');
        force(input, 'border', '1px solid rgba(242, 106, 31, 0.35)');
        force(input, 'caret-color', EMBER);
        force(input, 'font-family', "'IBM Plex Mono', monospace");
      });

      // Accordion hairlines — ember-tinted
      host.querySelectorAll('.accordion').forEach(function (acc) {
        force(acc, 'border-color', 'rgba(242, 106, 31, 0.25)');
        force(acc, 'border-bottom-color', 'rgba(242, 106, 31, 0.25)');
      });
    });
  }

  function fixAll(root) {
    fixSearch(root);
    fixFilters(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { fixAll(); });
  } else {
    fixAll();
  }

  var mo = new MutationObserver(function (mutations) {
    var needsSearch = false;
    var needsFilters = false;
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && (
          node.matches('search-drawer, .search-drawer, .search-input, predictive-search, input[type="search"]') ||
          (node.querySelector && node.querySelector('search-drawer, .search-drawer, .search-input, input[type="search"]'))
        )) needsSearch = true;
        if (node.matches && (
          node.matches('facet-form, .facets-vertical, .accordion') ||
          (node.querySelector && node.querySelector('facet-form, .facets-vertical'))
        )) needsFilters = true;
      });
    });
    if (needsSearch) fixSearch();
    if (needsFilters) fixFilters();
  });
  mo.observe(document.body, { childList: true, subtree: true });

  ['drawer:open', 'predictive-search:open', 'section:reload',
   'shopify:section:load', 'shopify:section:select'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(fixAll, 50);
    }, true);
  });

  setTimeout(fixAll, 200);
  setTimeout(fixAll, 800);
})();
